import { useCallback, useMemo, useState } from 'react'
import { useNanoRouter } from './use-nano-router'
import {
  type StorybookSettings,
  defaultStorybookSettings,
} from './storybook-settings'
import { parseStorybookRules, serializeStorybookRules } from './storybook-rules'

type FakeNetwork = StorybookSettings['fakeNetwork']
export const STORYBOOK_SIDEBAR_UNPINNED_KEY = 'storybook-sidebar-unpinned'

/**
 * Hook to read/write shareable storybook settings from URL params and local UI
 * preferences from localStorage.
 * Params: network=off|fast|slow|fail, lang=en|ru, theme=light|dark,
 * reversed=1, minimal=0|1, color=rrggbb, rules=v100,h200
 * preciseControl: pc=1, controlledEndpoints: endpoints=foo,bar
 */
export function useStorybookUrlSettings() {
  const { searchParams, navigate } = useNanoRouter()
  const [sidebarPinned, setSidebarPinned] = useState(getSidebarPinned)

  const settings = useMemo((): StorybookSettings => {
    const defaults = defaultStorybookSettings()

    const networkType = searchParams.get('network')
    const lang = searchParams.get('lang')
    const theme = searchParams.get('theme')
    const reversed = searchParams.get('reversed')
    const minimal = searchParams.get('minimal')
    const sourceColor = getSourceColor(searchParams.get('color'))
    const pc = searchParams.get('pc')
    const endpoints = searchParams.get('endpoints')

    return {
      fakeNetwork: {
        type: isValidNetwork(networkType)
          ? networkType
          : defaults.fakeNetwork.type,
        preciseControl: pc === '1',
        controlledEndpoints: endpoints
          ? endpoints.split(',').filter(Boolean)
          : [],
      },
      language: isValidLang(lang) ? lang : defaults.language,
      theme: isValidTheme(theme) ? theme : defaults.theme,
      reversedTheme: reversed === '1',
      minimal: getMinimal(minimal, defaults.minimal),
      sourceColor: sourceColor ?? defaults.sourceColor,
      sidebarPinned,
      rules: parseStorybookRules(searchParams.get('rules')),
    }
  }, [searchParams, sidebarPinned])

  const setSettings = useCallback(
    (
      newSettings:
        | StorybookSettings
        | ((prev: StorybookSettings) => StorybookSettings),
      options?: { replace?: boolean },
    ) => {
      const updated =
        typeof newSettings === 'function'
          ? newSettings(getUrlStorybookSettings())
          : newSettings
      const defaults = defaultStorybookSettings()

      // Build new URL preserving id param
      const params = new URLSearchParams(location.search)

      // Network
      if (updated.fakeNetwork.type === defaults.fakeNetwork.type) {
        params.delete('network')
      } else {
        params.set('network', updated.fakeNetwork.type)
      }

      // Precise control
      if (updated.fakeNetwork.preciseControl) {
        params.set('pc', '1')
      } else {
        params.delete('pc')
      }

      // Controlled endpoints
      if (updated.fakeNetwork.controlledEndpoints.length > 0) {
        params.set(
          'endpoints',
          updated.fakeNetwork.controlledEndpoints.join(','),
        )
      } else {
        params.delete('endpoints')
      }

      // Language
      if (updated.language === defaults.language) {
        params.delete('lang')
      } else {
        params.set('lang', updated.language)
      }

      // Theme
      if (updated.theme === defaults.theme) {
        params.delete('theme')
      } else {
        params.set('theme', updated.theme)
      }

      // Reversed theme
      if (updated.reversedTheme) {
        params.set('reversed', '1')
      } else {
        params.delete('reversed')
      }

      // Palette
      if (updated.minimal === defaults.minimal) {
        params.delete('minimal')
      } else {
        params.set('minimal', updated.minimal ? '1' : '0')
      }
      if (updated.sourceColor === defaults.sourceColor) {
        params.delete('color')
      } else {
        params.set('color', updated.sourceColor.slice(1).toLowerCase())
      }

      // Visual rules
      if (updated.rules.length > 0) {
        params.set('rules', serializeStorybookRules(updated.rules))
      } else {
        params.delete('rules')
      }

      // Sidebar pin mode is a local UI preference, not shareable URL state.
      localStorage.setItem(
        STORYBOOK_SIDEBAR_UNPINNED_KEY,
        updated.sidebarPinned ? '0' : '1',
      )
      setSidebarPinned(updated.sidebarPinned)
      params.delete('unpinned')

      navigate(`?${params.toString()}`, options)
    },
    [navigate],
  )

  return [settings, setSettings] as const
}

function isValidNetwork(v: string | null): v is FakeNetwork['type'] {
  return v === 'off' || v === 'fast' || v === 'slow' || v === 'fail'
}

function isValidLang(v: string | null): v is 'en' | 'ru' {
  return v === 'en' || v === 'ru'
}

function isValidTheme(v: string | null): v is 'light' | 'dark' {
  return v === 'light' || v === 'dark'
}

function getSourceColor(value: string | null) {
  if (!(value && /^[\da-f]{6}$/iu.test(value))) return null
  return `#${value.toLowerCase()}`
}

function getMinimal(value: string | null, fallback: boolean) {
  if (value === '0') return false
  if (value === '1') return true
  return fallback
}

function getSidebarPinned() {
  const stored = localStorage.getItem(STORYBOOK_SIDEBAR_UNPINNED_KEY)
  if (stored === null) return defaultStorybookSettings().sidebarPinned
  return stored !== '1'
}

/**
 * Non-hook version to get settings from URL (for use in getFakeNetworkStatus)
 */
export function getUrlStorybookSettings(): StorybookSettings {
  const defaults = defaultStorybookSettings()
  const params = new URLSearchParams(location.search)

  const networkType = params.get('network')
  const lang = params.get('lang')
  const theme = params.get('theme')
  const reversed = params.get('reversed')
  const minimal = params.get('minimal')
  const sourceColor = getSourceColor(params.get('color'))
  const pc = params.get('pc')
  const endpoints = params.get('endpoints')

  return {
    fakeNetwork: {
      type: isValidNetwork(networkType)
        ? networkType
        : defaults.fakeNetwork.type,
      preciseControl: pc === '1',
      controlledEndpoints: endpoints
        ? endpoints.split(',').filter(Boolean)
        : [],
    },
    language: isValidLang(lang) ? lang : defaults.language,
    theme: isValidTheme(theme) ? theme : defaults.theme,
    reversedTheme: reversed === '1',
    minimal: getMinimal(minimal, defaults.minimal),
    sourceColor: sourceColor ?? defaults.sourceColor,
    sidebarPinned: getSidebarPinned(),
    rules: parseStorybookRules(params.get('rules')),
  }
}
